import React, { useCallback, useMemo, useState } from "react";
import { FormValidation } from "react-jsonschema-form";
import { useIntl } from "react-intl";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { useKeycloak } from "../../components/keycloak-provider";
import actions from "../../lib/actions";
import { IConfig, useConfig } from "../../lib/config";
import { useHttpClient } from "../../lib/http";
import { CustomToast } from "../ariManagement/shared/toast";
import { CreatePropertyOptions } from "../createProperty/domain/createProperty.entity";
import CrudForm from "../shared/crud/form";
import { ReplicatePropertyEntity } from "./domain/replicateProperty.entity";
import { buildFormConfiguration } from "./domain/replicateProperty.formConfiguration";
import schema from "./domain/replicateProperty.schema.json";
import { executeReplicate } from "./infrastructure/replicateProperty.api";
import { messages } from "./messages";

function ReplicateProperty() {
  const { replicatePropertyEndpoint }: IConfig = useConfig();
  const keycloak = useKeycloak();
  const intl = useIntl();
  const replicatePropertyClient = useHttpClient(replicatePropertyEndpoint);

  const [toastType, setToastType] = useState<"success" | "error" | undefined>(undefined);

  const formConfiguration = useMemo(() => buildFormConfiguration(), []);

  const validate = useCallback(
    (formData: ReplicatePropertyEntity, errors: FormValidation) => {
      const { options } = formData;
      if (
        options.includes(CreatePropertyOptions.MIGRATE_ARI) &&
        !options.includes(CreatePropertyOptions.MIGRATE_INVENTORY)
      ) {
        errors.options.addError(intl.formatMessage(messages.ariRequiresInventory));
      }
      return errors;
    },
    [intl]
  );

  const handleGet = useCallback(() => {
    return Promise.resolve<ReplicatePropertyEntity>({
      id: "",
      originPropertyTicker: "demo_soporte",
      targetPropertyTicker: "",
      options: [],
    });
  }, []);

  const handleCreate: (entity: ReplicatePropertyEntity) => Promise<string> = useCallback(
    async ({ originPropertyTicker, targetPropertyTicker, options }) => {
      const emails = keycloak.profile?.email ? [keycloak.profile?.email] : undefined;
      const migrateInventory = options.includes(CreatePropertyOptions.MIGRATE_INVENTORY);
      const migrateAri = options.includes(CreatePropertyOptions.MIGRATE_ARI);

      await executeReplicate(
        replicatePropertyClient,
        originPropertyTicker,
        targetPropertyTicker,
        {
          migrateInventory,
          migrateAri,
        },
        emails
      );
      setToastType("success");
      return "success";
    },
    [keycloak.profile?.email, replicatePropertyClient]
  );

  return (
    <>
      <CrudForm<ReplicatePropertyEntity>
        id={"replicateProperty"}
        schema={schema}
        formConfiguration={formConfiguration}
        messages={messages}
        onCreate={handleCreate}
        onGet={handleGet}
        canCreate={actions.ReplicateProperty}
        showDirtyMessage={false}
        validate={validate}
      />
      <CustomToast
        show={!!toastType}
        type={toastType}
        successfulMessage={messages.successful}
        onClose={() => setToastType(undefined)}
      />
    </>
  );
}

export default withAuthorizationVerifier(ReplicateProperty);
